<template>
  <!-- Mobile View: Wrap in Expansion Panel -->
  <template v-if="!mdAndUp">
    <v-expansion-panels
      v-model="mobileNavExpanded"
      class="custom-panels mobile-nav-panel"
      elevation="0"
    >
      <v-expansion-panel class="filter-section" elevation="0">
        <v-expansion-panel-title class="pa-0">
          <div
            class="d-flex justify-space-between align-center py-1 w-100 px-2"
          >
            <h5 class="text-secondary font-weight-bold">
              {{ title || 'Navigation' }}
            </h5>
          </div>
        </v-expansion-panel-title>
        <v-divider v-if="mobileNavExpanded === 0" class="mt-1"></v-divider>
        <v-expansion-panel-text class="filter-content">
          <!-- Original v-list goes here -->
          <v-list
            density="compact"
            nav
            bg-color="white"
            active-color="primary"
            class="page-side-navigation-list p-0 nested-list"
            :opened="openGroups"
            @update:opened="updateOpenedGroups"
            :rounded="0"
          >
            <template
              v-for="(item, index) in navigationItems"
              :key="item.value || index"
            >
              <v-list-group
                v-if="item.children?.length && item.isGroup"
                :value="item.value"
              >
                <template v-slot:activator="{ props: activatorProps, isOpen }">
                  <v-list-item
                    v-bind="activatorProps"
                    :value="item.value"
                    :class="[
                      'navigation-item',
                      item.class,
                      'group-activator',
                      { 'has-border-top': item.borderTop },
                      { 'has-border-bottom': item.borderBottom && !isOpen },
                      { 'is-inactive': !!item.isInactive },
                    ]"
                    :active="isGroupActive(item)"
                    :rounded="0"
                  >
                    <template v-slot:title>
                      <div class="title-container">
                        <h4
                          v-if="item.isH4"
                          class="text-secondary list-item-h4"
                        >
                          {{ item.title }}
                        </h4>
                        <span v-else>{{ item.title }}</span>
                        <v-icon
                          v-if="item.titleIcon"
                          :icon="item.titleIcon"
                          size="x-small"
                          class="title-icon"
                        ></v-icon>
                      </div>
                    </template>
                    <template v-slot:append v-if="item.append">
                      <span class="append-text">
                        <span :class="item.appendClasses">{{
                          item.append
                        }}</span>
                      </span>
                    </template>
                  </v-list-item>
                </template>

                <template
                  v-for="(child, childIndex) in item.children"
                  :key="child.value"
                >
                  <v-list-group
                    v-if="child.isGroup && child.children?.length"
                    :value="child.value"
                  >
                    <template
                      v-slot:activator="{
                        props: childActivatorProps,
                        isOpen: isChildOpen,
                      }"
                    >
                      <v-list-item
                        v-bind="childActivatorProps"
                        :value="child.value"
                        :class="[
                          'navigation-item',
                          child.class,
                          'group-activator',
                          { 'pl-4': item.indentChildren },
                          { 'has-border-top': child.borderTop },
                          {
                            'has-border-bottom':
                              child.borderBottom && !isChildOpen,
                          },
                          { 'is-inactive': !!child.isInactive },
                        ]"
                        :active="isGroupActive(child)"
                        :rounded="0"
                      >
                        <template v-slot:title>
                          <div class="title-container">
                            <h4
                              v-if="child.isH4"
                              class="text-secondary list-item-h4"
                            >
                              {{ child.title }}
                            </h4>
                            <span v-else>{{ child.title }}</span>
                            <v-icon
                              v-if="child.titleIcon"
                              :icon="child.titleIcon"
                              size="x-small"
                              class="title-icon"
                            ></v-icon>
                          </div>
                        </template>
                        <template v-slot:append v-if="child.append">
                          <span class="append-text">
                            <span :class="child.appendClasses">{{
                              child.append
                            }}</span>
                          </span>
                        </template>
                      </v-list-item>
                    </template>
                    <v-list-item
                      v-for="(grandChild, grandChildIndex) in child.children"
                      :key="grandChild.value"
                      :value="grandChild.value"
                      :color="grandChild.color"
                      :class="[
                        'navigation-item',
                        grandChild.class,
                        { 'pl-4': child.indentChildren },
                        { 'has-border-top': grandChild.borderTop },
                        {
                          'has-border-bottom':
                            child.borderBottom &&
                            openGroups.includes(child.value) &&
                            grandChildIndex === child.children.length - 1,
                        },
                        { 'is-inactive': !!grandChild.isInactive },
                      ]"
                      :active="activeItem === grandChild.value"
                      @click="handleClick(grandChild)"
                      :rounded="0"
                    >
                      <template v-slot:title>
                        <div class="title-container">
                          <h4
                            v-if="grandChild.isH4"
                            class="text-secondary list-item-h4"
                          >
                            {{ grandChild.title }}
                          </h4>
                          <span v-else>{{ grandChild.title }}</span>
                          <v-icon
                            v-if="grandChild.titleIcon"
                            :icon="grandChild.titleIcon"
                            size="x-small"
                            class="title-icon"
                          ></v-icon>
                        </div>
                      </template>
                      <template v-slot:append v-if="grandChild.append">
                        <span class="append-text">
                          <span :class="grandChild.appendClasses">{{
                            grandChild.append
                          }}</span>
                        </span>
                      </template>
                    </v-list-item>
                  </v-list-group>

                  <v-list-item
                    v-else
                    :key="child.value"
                    :value="child.value"
                    :color="child.color"
                    :class="[
                      'navigation-item',
                      child.class,
                      { 'pl-4': item.indentChildren },
                      { 'has-border-top': child.borderTop },
                      {
                        'has-border-bottom':
                          item.borderBottom &&
                          openGroups.includes(item.value) &&
                          childIndex === item.children.length - 1,
                      },
                      { 'is-inactive': !!child.isInactive },
                    ]"
                    :active="activeItem === child.value"
                    @click="handleClick(child)"
                    :rounded="0"
                  >
                    <template v-slot:title>
                      <div class="title-container">
                        <h4
                          v-if="child.isH4"
                          class="text-secondary list-item-h4"
                        >
                          {{ child.title }}
                        </h4>
                        <span v-else>{{ child.title }}</span>
                        <v-icon
                          v-if="child.titleIcon"
                          :icon="child.titleIcon"
                          size="x-small"
                          class="title-icon"
                        ></v-icon>
                      </div>
                    </template>
                    <template v-slot:append v-if="child.append">
                      <span class="append-text">
                        <span :class="child.appendClasses">{{
                          child.append
                        }}</span>
                      </span>
                    </template>
                  </v-list-item>
                </template>
              </v-list-group>

              <template v-if="!item.isGroup">
                <v-list-item
                  :value="item.value"
                  :class="[
                    'navigation-item',
                    item.class,
                    { 'has-border-top': item.borderTop },
                    {
                      'has-border-bottom':
                        item.borderBottom && !item.children?.length,
                    },
                    { 'is-inactive': !!item.isInactive },
                  ]"
                  :active="activeItem === item.value"
                  @click="handleClick(item)"
                  :rounded="0"
                >
                  <template v-slot:title>
                    <div class="title-container">
                      <h4 v-if="item.isH4" class="text-secondary list-item-h4">
                        {{ item.title }}
                      </h4>
                      <span v-else>{{ item.title }}</span>
                      <v-icon
                        v-if="item.titleIcon"
                        :icon="item.titleIcon"
                        size="x-small"
                        class="title-icon"
                      ></v-icon>
                    </div>
                  </template>
                  <template v-slot:append v-if="item.append">
                    <span class="append-text">
                      <span :class="item.appendClasses">{{ item.append }}</span>
                    </span>
                  </template>
                </v-list-item>
                <template v-if="item.children?.length">
                  <v-list-item
                    v-for="(child, childIndex) in item.children"
                    :key="child.value"
                    :value="child.value"
                    :color="child.color"
                    :class="[
                      'navigation-item',
                      child.class,
                      { 'pl-4': item.indentChildren },
                      { 'has-border-top': child.borderTop },
                      {
                        'has-border-bottom':
                          item.borderBottom &&
                          childIndex === item.children.length - 1,
                      },
                      { 'is-inactive': !!child.isInactive },
                    ]"
                    :active="activeItem === child.value"
                    @click="handleClick(child)"
                    :rounded="0"
                  >
                    <template v-slot:title>
                      <div class="title-container">
                        <h4
                          v-if="child.isH4"
                          class="text-secondary list-item-h4"
                        >
                          {{ child.title }}
                        </h4>
                        <span v-else>{{ child.title }}</span>
                        <v-icon
                          v-if="child.titleIcon"
                          :icon="child.titleIcon"
                          size="x-small"
                          class="title-icon"
                        ></v-icon>
                      </div>
                    </template>
                    <template v-slot:append v-if="child.append">
                      <span class="append-text">
                        <span :class="child.appendClasses">{{
                          child.append
                        }}</span>
                      </span>
                    </template>
                  </v-list-item>
                </template>
              </template>
            </template>
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </template>

  <!-- Desktop View: Original v-list -->
  <v-list
    v-else
    density="compact"
    nav
    bg-color="white"
    active-color="primary"
    class="page-side-navigation-list p-0 desktop-nav-list"
    :opened="openGroups"
    @update:opened="updateOpenedGroups"
    :rounded="0"
  >
    <v-list-subheader v-if="title" class="navigation-header p-0">
      <h4 class="text-secondary px-2 py-4">{{ title }}</h4>
    </v-list-subheader>

    <template
      v-for="(item, index) in navigationItems"
      :key="item.value || index"
    >
      <v-list-group
        v-if="item.children?.length && item.isGroup"
        :value="item.value"
      >
        <template v-slot:activator="{ props: activatorProps, isOpen }">
          <v-list-item
            v-bind="activatorProps"
            :value="item.value"
            :class="[
              'navigation-item',
              item.class,
              'group-activator',
              { 'has-border-top': item.borderTop },
              { 'has-border-bottom': item.borderBottom && !isOpen },
              { 'is-inactive': !!item.isInactive },
            ]"
            :active="isGroupActive(item)"
            :rounded="0"
          >
            <template v-slot:title>
              <div class="title-container">
                <h4 v-if="item.isH4" class="text-secondary list-item-h4">
                  {{ item.title }}
                </h4>
                <span v-else>{{ item.title }}</span>
                <v-icon
                  v-if="item.titleIcon"
                  :icon="item.titleIcon"
                  size="x-small"
                  class="title-icon"
                ></v-icon>
              </div>
            </template>
            <template v-slot:append v-if="item.append">
              <span class="append-text">
                <span :class="item.appendClasses">{{ item.append }}</span>
              </span>
            </template>
          </v-list-item>
        </template>

        <template
          v-for="(child, childIndex) in item.children"
          :key="child.value"
        >
          <v-list-group
            v-if="child.isGroup && child.children?.length"
            :value="child.value"
          >
            <template
              v-slot:activator="{
                props: childActivatorProps,
                isOpen: isChildOpen,
              }"
            >
              <v-list-item
                v-bind="childActivatorProps"
                :value="child.value"
                :class="[
                  'navigation-item',
                  child.class,
                  'group-activator',
                  { 'pl-4': item.indentChildren },
                  { 'has-border-top': child.borderTop },
                  { 'has-border-bottom': child.borderBottom && !isChildOpen },
                  { 'is-inactive': !!child.isInactive },
                ]"
                :active="isGroupActive(child)"
                :rounded="0"
              >
                <template v-slot:title>
                  <div class="title-container">
                    <h4 v-if="child.isH4" class="text-secondary list-item-h4">
                      {{ child.title }}
                    </h4>
                    <span v-else>{{ child.title }}</span>
                    <v-icon
                      v-if="child.titleIcon"
                      :icon="child.titleIcon"
                      size="x-small"
                      class="title-icon"
                    ></v-icon>
                  </div>
                </template>
                <template v-slot:append v-if="child.append">
                  <span class="append-text">
                    <span :class="child.appendClasses">{{ child.append }}</span>
                  </span>
                </template>
              </v-list-item>
            </template>
            <v-list-item
              v-for="(grandChild, grandChildIndex) in child.children"
              :key="grandChild.value"
              :value="grandChild.value"
              :color="grandChild.color"
              :class="[
                'navigation-item',
                grandChild.class,
                { 'pl-4': child.indentChildren },
                { 'has-border-top': grandChild.borderTop },
                {
                  'has-border-bottom':
                    child.borderBottom &&
                    openGroups.includes(child.value) &&
                    grandChildIndex === child.children.length - 1,
                },
                { 'is-inactive': !!grandChild.isInactive },
              ]"
              :active="activeItem === grandChild.value"
              @click="handleClick(grandChild)"
              :rounded="0"
            >
              <template v-slot:title>
                <div class="title-container">
                  <h4
                    v-if="grandChild.isH4"
                    class="text-secondary list-item-h4"
                  >
                    {{ grandChild.title }}
                  </h4>
                  <span v-else>{{ grandChild.title }}</span>
                  <v-icon
                    v-if="grandChild.titleIcon"
                    :icon="grandChild.titleIcon"
                    size="x-small"
                    class="title-icon"
                  ></v-icon>
                </div>
              </template>
              <template v-slot:append v-if="grandChild.append">
                <span class="append-text">
                  <span :class="grandChild.appendClasses">{{
                    grandChild.append
                  }}</span>
                </span>
              </template>
            </v-list-item>
          </v-list-group>

          <v-list-item
            v-else
            :key="child.value"
            :value="child.value"
            :color="child.color"
            :class="[
              'navigation-item',
              child.class,
              { 'pl-4': item.indentChildren },
              { 'has-border-top': child.borderTop },
              {
                'has-border-bottom':
                  item.borderBottom &&
                  openGroups.includes(item.value) &&
                  childIndex === item.children.length - 1,
              },
              { 'is-inactive': !!child.isInactive },
            ]"
            :active="activeItem === child.value"
            @click="handleClick(child)"
            :rounded="0"
          >
            <template v-slot:title>
              <div class="title-container">
                <h4 v-if="child.isH4" class="text-secondary list-item-h4">
                  {{ child.title }}
                </h4>
                <span v-else>{{ child.title }}</span>
                <v-icon
                  v-if="child.titleIcon"
                  :icon="child.titleIcon"
                  size="x-small"
                  class="title-icon"
                ></v-icon>
              </div>
            </template>
            <template v-slot:append v-if="child.append">
              <span class="append-text">
                <span :class="child.appendClasses">{{ child.append }}</span>
              </span>
            </template>
          </v-list-item>
        </template>
      </v-list-group>

      <template v-if="!item.isGroup">
        <v-list-item
          :value="item.value"
          :class="[
            'navigation-item',
            item.class,
            { 'has-border-top': item.borderTop },
            {
              'has-border-bottom': item.borderBottom && !item.children?.length,
            },
            { 'is-inactive': !!item.isInactive },
          ]"
          :active="activeItem === item.value"
          @click="handleClick(item)"
          :rounded="0"
        >
          <template v-slot:title>
            <div class="title-container">
              <h4 v-if="item.isH4" class="text-secondary list-item-h4">
                {{ item.title }}
              </h4>
              <span v-else>{{ item.title }}</span>
              <v-icon
                v-if="item.titleIcon"
                :icon="item.titleIcon"
                size="x-small"
                class="title-icon"
              ></v-icon>
            </div>
          </template>
          <template v-slot:append v-if="item.append">
            <span class="append-text">
              <span :class="item.appendClasses">{{ item.append }}</span>
            </span>
          </template>
        </v-list-item>
        <template v-if="item.children?.length">
          <v-list-item
            v-for="(child, childIndex) in item.children"
            :key="child.value"
            :value="child.value"
            :color="child.color"
            :class="[
              'navigation-item',
              child.class,
              { 'pl-4': item.indentChildren },
              { 'has-border-top': child.borderTop },
              {
                'has-border-bottom':
                  item.borderBottom && childIndex === item.children.length - 1,
              },
              { 'is-inactive': !!child.isInactive },
            ]"
            :active="activeItem === child.value"
            @click="handleClick(child)"
            :rounded="0"
          >
            <template v-slot:title>
              <div class="title-container">
                <h4 v-if="child.isH4" class="text-secondary list-item-h4">
                  {{ child.title }}
                </h4>
                <span v-else>{{ child.title }}</span>
                <v-icon
                  v-if="child.titleIcon"
                  :icon="child.titleIcon"
                  size="x-small"
                  class="title-icon"
                ></v-icon>
              </div>
            </template>
            <template v-slot:append v-if="child.append">
              <span class="append-text">
                <span :class="child.appendClasses">{{ child.append }}</span>
              </span>
            </template>
          </v-list-item>
        </template>
      </template>
    </template>
  </v-list>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useDisplay } from 'vuetify';

  // --- Responsive Display --- //
  const display = useDisplay();
  const mdAndUp = computed(() => display.mdAndUp.value);
  const mobileNavExpanded = ref<number | null>(null); // Use number for single panel index

  /** Represents a single navigation item or group and its properties */
  interface NavigationItem {
    title: string;
    value: string;
    color?: string;
    class?: string;
    children?: NavigationItem[];
    isGroup?: boolean;
    indentChildren?: boolean;
    isH4?: boolean;
    borderTop?: boolean;
    borderBottom?: boolean;
    append?: string;
    appendClasses?: string | string[] | object;
    titleIcon?: string;
    isInactive?: boolean;
    route?: {
      name: string;
      params?: Record<string, string>;
    };
  }

  interface Props {
    title?: string;
    navigationItems: NavigationItem[];
    activeItem: string;
    initiallyOpenGroups?: string[];
  }

  const props = withDefaults(defineProps<Props>(), {
    title: '',
    initiallyOpenGroups: () => [],
  });

  const emit = defineEmits<{
    (e: 'update:activeItem', value: string): void;
    (e: 'itemClick', item: NavigationItem): void;
  }>();

  const openGroups = ref<string[]>(props.initiallyOpenGroups);

  watch(
    () => props.initiallyOpenGroups,
    (newVal) => {
      openGroups.value = newVal;
    },
  );

  const updateOpenedGroups = (newOpened: string[]) => {
    openGroups.value = newOpened;
  };

  const handleClick = (item: NavigationItem) => {
    if (!item.isInactive) {
      handleItemClick(item);
    }
  };

  const handleItemClick = (item: NavigationItem) => {
    emit('update:activeItem', item.value);
    emit('itemClick', item);
  };

  const isGroupActive = (groupItem: NavigationItem): boolean => {
    if (!groupItem.children?.length) return false;
    return groupItem.children.some(
      (child) =>
        child.value === props.activeItem ||
        (child.isGroup && isGroupActive(child)),
    );
  };
</script>

<style lang="scss" scoped>
  // --- Mobile Expansion Panel Styles (similar to ReportsAllFilters) --- //
  .custom-panels {
    background: transparent !important;
    box-shadow: none !important;
    contain: layout style;

    &.mobile-nav-panel {
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 3px;
    }

    :deep(.v-expansion-panel) {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
      contain: content;

      .v-expansion-panel-title__icon {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }

    :deep(.v-expansion-panel-title) {
      min-height: unset !important;
      border: none !important;
      touch-action: manipulation;
    }

    :deep(.v-expansion-panel-title__overlay) {
      display: none;
    }

    :deep(.v-expansion-panel-title--active) {
      min-height: unset !important;
      border: none !important;
    }

    :deep(.v-expansion-panel-text__wrapper) {
      padding: 0;
    }

    :deep(.v-expansion-panel::after) {
      border: none !important;
    }

    .filter-section {
      // border-bottom: 1px solid rgba(0, 0, 0, 0.12); // Maybe remove border inside panel
      contain: content;
    }

    .filter-content {
      padding-bottom: 0; // Remove extra padding if list has its own
      contain: content;
    }
  }

  // --- Original PageSideNav Styles --- //
  .page-side-navigation-list {
    border-radius: 3px !important;
    border: 1px solid rgba(0, 0, 0, 0.12);

    // Remove border when nested inside the expansion panel
    &.nested-list {
      border: none !important;
      border-radius: 0 !important;
    }

    .navigation-header h4 {
      font-weight: bold;
    }

    .append-text {
      font-size: 0.875rem;
      color: rgb(var(--v-theme-secondary));
      margin-left: auto;
      padding-left: 8px;
      white-space: normal;
    }

    .list-item-h4 {
      font-weight: bold !important;
      line-height: 1.2 !important;
      margin: 0;
      padding: 0;
      color: rgb(var(--v-theme-secondary));
      white-space: normal;
    }

    .title-container {
      display: flex;
      align-items: flex-start;
      width: 100%;
      color: rgb(var(--v-theme-primary));
      white-space: normal;
    }

    .title-icon {
      margin-left: 8px;
      color: rgb(var(--v-theme-meta-grey));
      vertical-align: middle;
      flex-shrink: 0;
    }

    :deep(.v-list-item__content) {
      align-self: stretch;
      display: flex;
      align-items: center;
      width: 100%;
    }

    :deep(.navigation-item) {
      position: relative;
      min-height: 40px;
      padding-left: 16px !important;
      padding-right: 16px !important;
      margin-bottom: 0 !important;
      white-space: normal;

      &.has-border-top {
        border-top: 1px solid rgba(0, 0, 0, 0.12);
      }
      &.has-border-bottom {
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      }

      &.pl-4 {
        padding-left: 32px !important;
      }

      &.is-inactive {
        pointer-events: none;
        cursor: default;

        .title-container span,
        .title-container .list-item-h4 {
          color: #000 !important;
        }

        .append-text,
        .title-icon {
          color: #000 !important;
        }

        &:hover {
          .title-container span,
          .title-container .list-item-h4 {
            color: #000 !important;
          }
        }

        &::before {
          display: none !important;
        }

        .v-list-item__overlay {
          display: none !important;
        }
      }

      &.v-list-item--active:not(.group-activator):not(.is-inactive) {
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background-color: rgb(var(--v-theme-blue));
        }
        .title-container {
          color: rgb(var(--v-theme-blue)) !important;
        }
        .list-item-h4 {
          color: rgb(var(--v-theme-blue)) !important;
        }
      }

      .v-list-item-title {
        flex-grow: 1;
        padding-top: 8px;
        padding-bottom: 8px;
        margin-right: 8px;
        > span {
          font-size: 0.875rem;
          white-space: normal;
          line-height: 1.2;
        }
      }

      &.font-weight-bold {
        .title-container span,
        .list-item-h4 {
          font-weight: bold !important;
        }
      }
      &.font-weight-medium {
        .title-container span,
        .list-item-h4 {
          font-weight: 500 !important;
        }
      }

      &.group-activator {
        &.v-list-item--active {
          background-color: transparent !important;
        }
      }

      &.v-list-item--rounded {
        border-radius: 0 !important;
      }
    }
  }
</style>
